// ===================================================================
// components/MiniGames.tsx
// Mini-jeux : Quiz Culturel uniquement
// ===================================================================
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ChevronLeft, Check, X, ScrollText, Globe, Scale, Moon } from 'lucide-react';
import BackButton from './common/BackButton';
import { MINI_GAMES_DATA, QuizTheme, QuizQuestion } from '../data/miniGamesData';
import { quizQuestionText, quizOptionText, quizExplanationText, quizCountryName } from '../data/miniGamesEn';
import { LanguageCode } from '../types/types';
import { useTheme } from './ThemeContext';
import './MiniGames.css';
import TribalBorder from './TribalBorder';

import { EmojiText, Picto } from './common/Picto';
// ─── Props ────────────────────────────────────────────────────────
interface MiniGamesProps {
  languageCode: LanguageCode;
  onChallengeFriend?: (mode?: string) => void;
  pendingChallengesCount?: number;
  onLaunchSongAnalyzer?: () => void;
  onLaunchWordle?: () => void;
  onLaunchMemoryDuel?: () => void;
  onLaunchForge?: () => void;
  initialTheme?: QuizTheme;
  onBack?: () => void;
}

// ─── Constantes ───────────────────────────────────────────────────
const MAX_QUESTIONS = 10;

const THEME_META: Record<QuizTheme, { emoji: string; label: string; color: string; labelKey: string }> = {
  Histoire:     { emoji: '📜', label: 'Histoire',     color: '#c9a961', labelKey: 'miniGames.themes.history' },
  Politique:    { emoji: '🏛️', label: 'Politique',    color: '#8b4513', labelKey: 'miniGames.themes.politics' },
  Géographie:   { emoji: '🌍', label: 'Géographie',   color: '#2e6b3e', labelKey: 'miniGames.themes.geography' },
  Spiritualité: { emoji: '🕌', label: 'Spiritualité', color: 'var(--gold-dark)', labelKey: 'miniGames.themes.spirituality' },
};

// Icône trait par thème pour la pastille de l'en-tête du quiz (pas d'emoji)
const THEME_ICONS: Record<QuizTheme, React.ComponentType<{ size?: number | string; strokeWidth?: number | string }>> = {
  Histoire:     ScrollText,
  Politique:    Scale,
  Géographie:   Globe,
  Spiritualité: Moon,
};

// Accroche affichée sur la page d'accueil de chaque thème
const THEME_BLURB: Record<QuizTheme, string> = {
  Histoire:     'Héros, empires et grandes dates qui ont façonné le pays.',
  Politique:    'Indépendances, institutions et figures du pouvoir.',
  Géographie:   'Terres, fleuves, villes et réserves naturelles.',
  Spiritualité: 'Confréries, croyances et lieux saints.',
};

const LANG_TO_DATA_KEY: Record<LanguageCode, string> = {
  ba: 'bambara', wf: 'wolof', la: 'lingala', sw: 'swahili', ff: 'peul', co: 'copte',
  ka: 'kama', // pas encore de contenu mini-jeux pour Kama
  yo: 'yoruba',
};

// ─── Utilitaires ──────────────────────────────────────────────────
function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// ─── Types internes ───────────────────────────────────────────────
type Screen = 'theme-select' | 'intro' | 'quiz' | 'results';

interface SavedSession {
  questions: GameQuestion[];
  currentIndex: number;
  score: number;
}

interface AnswerState {
  selected: string | null;
  isCorrect: boolean | null;
}

interface GameQuestion extends QuizQuestion {
  theme?: string;
}

// ─── Composant principal ──────────────────────────────────────────
const MiniGames: React.FC<MiniGamesProps> = ({ languageCode, onChallengeFriend, pendingChallengesCount = 0, onLaunchSongAnalyzer, onLaunchWordle, onLaunchMemoryDuel, onLaunchForge, initialTheme, onBack }) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;

  const dataKey     = LANG_TO_DATA_KEY[languageCode] ?? languageCode;
  const countryData = MINI_GAMES_DATA[dataKey];
  const countryName = countryData ? quizCountryName(dataKey, countryData.countryName, i18n.language) : '';

  // ─── State ──────────────────────────────────────────────────────
  const [screen,        setScreen]        = useState<Screen>('theme-select');
  const [selectedTheme, setSelectedTheme] = useState<QuizTheme | null>(null);

  const [questions,    setQuestions]    = useState<GameQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score,        setScore]        = useState(0);
  const [answerState,  setAnswerState]  = useState<AnswerState>({ selected: null, isCorrect: null });
  // Réponse choisie mais pas encore validée (bouton « Valider »)
  const [pendingOption, setPendingOption] = useState<string | null>(null);

  // ─── Clé localStorage pour la progression en cours ───────────────
  const progressKey = useCallback(
    (t: QuizTheme) => `${languageCode}-quiz-inprogress-${t}`,
    [languageCode]
  );

  // ─── Auto-démarrage si thème fourni depuis le sentier ────────────
  useEffect(() => {
    if (initialTheme && countryData) handleThemeSelect(initialTheme);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Reset quiz state ─────────────────────────────────────────────
  const resetQuiz = useCallback(() => {
    setCurrentIndex(0);
    setScore(0);
    setAnswerState({ selected: null, isCorrect: null });
    setPendingOption(null);
  }, []);

  // ─── Retour sélection de thème ────────────────────────────────────
  const handleBackToThemeSelect = useCallback(() => {
    setScreen('theme-select');
    setSelectedTheme(null);
    setQuestions([]);
    resetQuiz();
  }, [resetQuiz]);

  // ─── Session en cours sauvegardée (moins de 24 h) ─────────────────
  const readSavedSession = useCallback((t: QuizTheme): SavedSession | null => {
    try {
      const saved = localStorage.getItem(progressKey(t));
      if (!saved) return null;
      const { questions: savedQs, currentIndex: savedIdx, score: savedScore, timestamp } = JSON.parse(saved);
      const fresh = Date.now() - timestamp < 24 * 60 * 60 * 1000;
      if (fresh && Array.isArray(savedQs) && savedQs.length > 0 && savedIdx < savedQs.length) {
        return { questions: savedQs, currentIndex: savedIdx, score: savedScore ?? 0 };
      }
    } catch { /* ignore */ }
    return null;
  }, [progressKey]);

  // ─── Quiz culturel : choisir un thème ouvre la page d'accueil ────
  const handleThemeSelect = useCallback((t: QuizTheme) => {
    if (!countryData) return;
    setSelectedTheme(t);
    setScreen('intro');
  }, [countryData]);

  // ─── Lancer le quiz (reprise ou nouvelle session) ─────────────────
  const startQuiz = useCallback((t: QuizTheme, resume: boolean) => {
    if (!countryData) return;

    const saved = resume ? readSavedSession(t) : null;
    if (saved) {
      setSelectedTheme(t);
      setQuestions(saved.questions);
      setCurrentIndex(saved.currentIndex);
      setScore(saved.score);
      setAnswerState({ selected: null, isCorrect: null });
      setPendingOption(null);
      setScreen('quiz');
      return;
    }

    // Nouvelle session
    const pool = countryData.themes[t] ?? [];
    const qs   = shuffle(pool).slice(0, MAX_QUESTIONS).map(q => ({ ...q, theme: t as string, options: shuffle(q.options) }));
    setSelectedTheme(t);
    setQuestions(qs);
    resetQuiz();
    setScreen('quiz');
    // Sauvegarder l'état initial
    try {
      localStorage.setItem(progressKey(t), JSON.stringify({ questions: qs, currentIndex: 0, score: 0, timestamp: Date.now() }));
    } catch { /* ignore */ }
  }, [countryData, resetQuiz, progressKey, readSavedSession]);

  // ─── Réponse quiz ────────────────────────────────────────────────
  // Appelé par « Valider » : affiche la correction ;
  // il faut ensuite cliquer sur "Suivant" pour passer à la question suivante.
  const handleAnswer = useCallback((option: string) => {
    if (answerState.selected !== null) return;
    const question  = questions[currentIndex];
    const isCorrect = option === question.answer;
    setAnswerState({ selected: option, isCorrect });
    if (isCorrect) setScore(s => s + 1);
  }, [answerState, questions, currentIndex]);

  // ─── Passer à la question suivante ───────────────────────────────
  const handleNext = useCallback(() => {
    if (answerState.selected === null) return;
    const nextIndex = currentIndex + 1;

    if (selectedTheme && nextIndex < questions.length) {
      try {
        localStorage.setItem(progressKey(selectedTheme), JSON.stringify({
          questions,
          currentIndex: nextIndex,
          score,
          timestamp: Date.now(),
        }));
      } catch { /* ignore */ }
    }

    if (nextIndex >= questions.length) {
      // Quiz terminé : supprimer l'état en cours, sauvegarder le meilleur score
      if (selectedTheme) {
        try {
          localStorage.removeItem(progressKey(selectedTheme));
          const bestKey = `${languageCode}-quiz-best-${selectedTheme}`;
          const prev = parseInt(localStorage.getItem(bestKey) || '0', 10);
          if (score > prev) localStorage.setItem(bestKey, String(score));
        } catch { /* ignore */ }
      }
      setScreen('results');
    } else {
      setCurrentIndex(nextIndex);
      setAnswerState({ selected: null, isCorrect: null });
      setPendingOption(null);
    }
  }, [answerState, currentIndex, questions, score, selectedTheme, progressKey, languageCode]);

  // ─── Rejouer ─────────────────────────────────────────────────────
  const handleReplay = useCallback(() => {
    if (selectedTheme) {
      try { localStorage.removeItem(progressKey(selectedTheme)); } catch { /* ignore */ }
      startQuiz(selectedTheme, false);
    }
  }, [selectedTheme, startQuiz, progressKey]);

  // ─── Données insuffisantes ────────────────────────────────────────
  if (!countryData) {
    return (
      <div className="minigames-container" data-theme={theme}>
        <div className="minigames-unavailable">
          <div className="minigames-unavailable-icon"><Picto name="monumentHistoire" /></div>
          <h2 className="minigames-unavailable-title">{t('miniGames.comingSoon')}</h2>
          <p className="minigames-unavailable-text">{t('miniGames.comingSoonText')}</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const percentage      = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;

  // ─── Rendu : sélection de thème ──────────────────────────────────
  if (screen === 'theme-select') {
    return (
      <div className="mg-screen" data-theme={theme}>
        {!isDesktop && <TribalBorder />}

        <div className="mg-sel-header">
          {onBack && (
            <div className="mg-sel-back-row">
              <BackButton onClick={onBack} />
            </div>
          )}
          <div className="mg-sel-header__arch">
            <span className="mg-sel-header__deco">◇ · ◇</span>
            <h1 className="mg-sel-header__title">
              {countryData.countryFlag} {countryName}
            </h1>
            <p className="mg-sel-header__subtitle">{t('miniGames.culturalQuiz')}</p>
          </div>
        </div>

        <div className="mg-sel-body">
          <p className="mg-sel-body__hint">{t('miniGames.chooseTheme')}</p>
          <div className="mg-sel-grid">
            {(Object.keys(THEME_META) as QuizTheme[])
              .filter(t => (countryData.themes[t]?.length ?? 0) > 0)
              .map((themeKey, idx) => {
                const meta  = THEME_META[themeKey];
                const count = countryData.themes[themeKey]?.length ?? 0;
                return (
                  <motion.div
                    key={themeKey}
                    onClick={() => handleThemeSelect(themeKey)}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.06 }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      background: theme === 'dark' ? '#A85C2A' : 'var(--gold-primary)',
                      borderRadius: '100px 100px 16px 16px / 44px 44px 16px 16px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      minHeight: '145px',
                      boxShadow: theme === 'dark'
                        ? '0 6px 24px rgba(0,0,0,0.5)'
                        : '0 6px 24px rgba(168,92,42,0.45)',
                    }}
                  >
                    <div style={{ width: '100%', padding: '26px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '34px', lineHeight: 1, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}><EmojiText text={meta.emoji} /></span>
                    </div>
                    <div style={{ padding: '8px 12px 16px', textAlign: 'center', width: '100%' }}>
                      <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>{t(meta.labelKey)}</h3>
                      <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px' }}>{t('miniGames.questionsCount', { count })}</span>
                    </div>
                  </motion.div>
                );
              })}

            {/* Carte Analyse de Chansons */}
            {onLaunchSongAnalyzer && (
              <motion.div
                onClick={onLaunchSongAnalyzer}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (Object.keys(THEME_META).length) * 0.06 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  background: theme === 'dark' ? '#1a4a2a' : '#2e7d32',
                  borderRadius: '100px 100px 16px 16px / 44px 44px 16px 16px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  minHeight: '145px',
                  boxShadow: theme === 'dark'
                    ? '0 6px 24px rgba(0,0,0,0.5)'
                    : '0 6px 24px rgba(46,125,50,0.45)',
                }}
              >
                <div style={{ width: '100%', padding: '26px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '34px', lineHeight: 1, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}><Picto name="musique" /></span>
                </div>
                <div style={{ padding: '8px 12px 16px', textAlign: 'center', width: '100%' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>{t('miniGames.songs')}</h3>
                  <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px' }}>{t('miniGames.analysis')}</span>
                </div>
              </motion.div>
            )}

            {/* Carte Mot du jour (Wordle africain) */}
            {onLaunchWordle && (
              <motion.div
                onClick={onLaunchWordle}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (Object.keys(THEME_META).length + (onLaunchSongAnalyzer ? 1 : 0)) * 0.06 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  background: theme === 'dark' ? '#1a3a4a' : '#2e6b7d',
                  borderRadius: '100px 100px 16px 16px / 44px 44px 16px 16px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  minHeight: '145px',
                  boxShadow: theme === 'dark'
                    ? '0 6px 24px rgba(0,0,0,0.5)'
                    : '0 6px 24px rgba(46,107,125,0.45)',
                }}
              >
                <div style={{ width: '100%', padding: '26px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '34px', lineHeight: 1, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}><Picto name="lettres" /></span>
                </div>
                <div style={{ padding: '8px 12px 16px', textAlign: 'center', width: '100%' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>{t('miniGames.wordleCard', 'Mot du jour')}</h3>
                  <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px' }}>{t('miniGames.daily', 'Quotidien')}</span>
                </div>
              </motion.div>
            )}

            {/* Carte Memory Duel */}
            {onLaunchMemoryDuel && (
              <motion.div
                onClick={onLaunchMemoryDuel}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (Object.keys(THEME_META).length + (onLaunchSongAnalyzer ? 2 : 1)) * 0.06 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  background: theme === 'dark' ? '#4a2670' : '#6b3fa0',
                  borderRadius: '100px 100px 16px 16px / 44px 44px 16px 16px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  minHeight: '145px',
                  boxShadow: theme === 'dark'
                    ? '0 6px 24px rgba(0,0,0,0.5)'
                    : '0 6px 24px rgba(107,63,160,0.45)',
                }}
              >
                <div style={{ width: '100%', padding: '26px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '34px', lineHeight: 1, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}><Picto name="cartesMemoire" /></span>
                </div>
                <div style={{ padding: '8px 12px 16px', textAlign: 'center', width: '100%' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>{t('miniGames.memoryCard', 'Memory Duel')}</h3>
                  <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px' }}>2 {t('miniGames.players', 'joueurs')}</span>
                </div>
              </motion.div>
            )}

            {/* Carte La forge des mots */}
            {onLaunchForge && languageCode !== 'ka' && (
              <motion.div
                onClick={onLaunchForge}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (Object.keys(THEME_META).length + (onLaunchSongAnalyzer ? 3 : 2)) * 0.06 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  background: theme === 'dark' ? '#6e3a1f' : '#C67B4E',
                  borderRadius: '100px 100px 16px 16px / 44px 44px 16px 16px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  minHeight: '145px',
                  boxShadow: theme === 'dark'
                    ? '0 6px 24px rgba(0,0,0,0.5)'
                    : '0 6px 24px rgba(142,74,38,0.45)',
                }}
              >
                <div style={{ width: '100%', padding: '26px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '34px', lineHeight: 1, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}><Picto name="feu" /></span>
                </div>
                <div style={{ padding: '8px 12px 16px', textAlign: 'center', width: '100%' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>{t('miniGames.forgeCard', 'La forge des mots')}</h3>
                  <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px' }}>{t('miniGames.forgeTag', 'Vocabulaire')}</span>
                </div>
              </motion.div>
            )}

            {/* Carte Défi ami */}
            {onChallengeFriend && (
              <motion.div
                onClick={() => onChallengeFriend('cultural')}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (Object.keys(THEME_META).length + 1) * 0.06 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  position: 'relative',
                  background: theme === 'dark' ? '#4a2670' : 'var(--gold-dark)',
                  borderRadius: '100px 100px 16px 16px / 44px 44px 16px 16px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  minHeight: '145px',
                  boxShadow: theme === 'dark'
                    ? '0 6px 24px rgba(0,0,0,0.5)'
                    : '0 6px 24px rgba(107,63,160,0.45)',
                }}
              >
                {pendingChallengesCount > 0 && (
                  <div style={{
                    position: 'absolute', top: '10px', right: '10px',
                    background: 'linear-gradient(135deg, var(--color-error), var(--color-error-dark))',
                    minWidth: '20px', height: '20px', borderRadius: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: '11px', fontWeight: 700, padding: '0 5px',
                    boxShadow: '0 0 6px rgba(239,68,68,0.6)',
                    zIndex: 1,
                  }}>
                    {pendingChallengesCount > 9 ? '9+' : pendingChallengesCount}
                  </div>
                )}
                <div style={{ width: '100%', padding: '26px 0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '34px', lineHeight: 1, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))' }}><Picto name="epeesDuel" /></span>
                </div>
                <div style={{ padding: '8px 12px 16px', textAlign: 'center', width: '100%' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>{t('miniGames.challengeFriend')}</h3>
                  <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px' }}>{t('miniGames.duel')}</span>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Rendu : page d'accueil du thème (avant de lancer le quiz) ───
  if (screen === 'intro' && selectedTheme) {
    const ThemeIcon  = THEME_ICONS[selectedTheme];
    const themeName  = t(THEME_META[selectedTheme].labelKey);
    const poolSize   = countryData.themes[selectedTheme]?.length ?? 0;
    const total      = Math.min(MAX_QUESTIONS, poolSize);
    const saved      = readSavedSession(selectedTheme);
    let best = 0;
    try { best = parseInt(localStorage.getItem(`${languageCode}-quiz-best-${selectedTheme}`) || '0', 10); } catch { /* ignore */ }

    return (
      <div className="cq" data-theme={theme}>
        <header className="cq-band">
          <div className="cq-band__row">
            <button className="cq-band__back" onClick={onBack ?? handleBackToThemeSelect} aria-label={t('miniGames.back') as string}>
              <ChevronLeft size={20} strokeWidth={2} />
            </button>
            <div className="cq-band__center">
              <span className="cq-band__pill">
                <ThemeIcon size={13} strokeWidth={1.8} />
                {themeName.toUpperCase()}
              </span>
            </div>
            <div className="cq-band__score cq-band__score--spacer" aria-hidden="true" />
          </div>
          <div className="cq-band__meta">
            <span>{t('miniGames.culturalQuiz')}</span>
            <span>{countryName.toUpperCase()}</span>
          </div>
        </header>
        <div className="cq-crenel" aria-hidden="true">
          {Array.from({ length: 8 }, (_, i) => <span key={i} />)}
        </div>

        <div className="cq-body">
          <div className="cq-niche cq-intro__hero">
            <div className="cq-intro__icon" aria-hidden="true">
              <ThemeIcon size={30} strokeWidth={1.6} />
            </div>
            <div className="cq-niche__kicker">{t('miniGames.culturalQuiz')} · {countryName.toUpperCase()}</div>
            <h1 className="cq-intro__title">{themeName}</h1>
            <p className="cq-intro__blurb">
              {t(`miniGames.intro.blurb.${THEME_META[selectedTheme].labelKey.split('.').pop()}`, { defaultValue: THEME_BLURB[selectedTheme] })}
            </p>
          </div>

          <div className="cq-intro__stats">
            <div className="cq-intro__stat">
              <span className="cq-intro__stat-value">{total}</span>
              <span className="cq-intro__stat-label">{t('miniGames.intro.questions', 'QUESTIONS')}</span>
            </div>
            <div className="cq-intro__stat">
              <span className="cq-intro__stat-value">{best}/{total}</span>
              <span className="cq-intro__stat-label">{t('miniGames.intro.best', 'RECORD')}</span>
            </div>
            <div className="cq-intro__stat">
              <span className="cq-intro__stat-value">{saved ? `${saved.currentIndex + 1}/${saved.questions.length}` : '—'}</span>
              <span className="cq-intro__stat-label">{t('miniGames.intro.inProgress', 'EN COURS')}</span>
            </div>
          </div>

        </div>

        <div className="cq-footer cq-intro__actions">
          <button type="button" className="cq-cta" onClick={() => startQuiz(selectedTheme, true)} disabled={total === 0}>
            {saved
              ? t('miniGames.intro.resume', { n: saved.currentIndex + 1, defaultValue: 'Reprendre à la question {{n}}' })
              : t('miniGames.intro.start', 'Commencer')}
          </button>
          {saved && (
            <button type="button" className="cq-cta cq-cta--ghost" onClick={() => startQuiz(selectedTheme, false)}>
              {t('miniGames.intro.restart', 'Recommencer à zéro')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Rendu : quiz ─────────────────────────────────────────────────
  if (screen === 'quiz' && currentQuestion && selectedTheme) {
    const ThemeIcon   = THEME_ICONS[selectedTheme];
    const hasAnswered = answerState.selected !== null;

    return (
      <div className="cq" data-theme={theme}>
        {/* En-tête crénelé : retour · thème · score, puis progression segmentée */}
        <header className="cq-band">
          <div className="cq-band__row">
            <button className="cq-band__back" onClick={onBack ?? handleBackToThemeSelect} aria-label={t('miniGames.back') as string}>
              <ChevronLeft size={20} strokeWidth={2} />
            </button>
            <div className="cq-band__center">
              <span className="cq-band__pill">
                <ThemeIcon size={13} strokeWidth={1.8} />
                {t(THEME_META[selectedTheme].labelKey).toUpperCase()}
              </span>
            </div>
            <div className="cq-band__score" aria-label={`${score} / ${questions.length}`}>
              {score}/{questions.length}
            </div>
          </div>
          <div
            className="cq-band__segments"
            role="progressbar"
            aria-valuenow={currentIndex + 1}
            aria-valuemin={1}
            aria-valuemax={questions.length}
          >
            {questions.map((_, i) => (
              <span key={i} className={`cq-band__seg${i <= currentIndex ? ' is-done' : ''}`} />
            ))}
          </div>
          <div className="cq-band__meta">
            <span>{t('miniGames.questionCounter', { current: currentIndex + 1, total: questions.length }).toUpperCase()}</span>
            <span>{countryName.toUpperCase()}</span>
          </div>
        </header>
        <div className="cq-crenel" aria-hidden="true">
          {Array.from({ length: 8 }, (_, i) => <span key={i} />)}
        </div>

        <div className="cq-body">
          {/* Niche : la question vit sous l'arche */}
          <div className={`cq-niche${hasAnswered ? ' is-compact' : ''}`}>
            <div className="cq-niche__kicker">
              {hasAnswered
                ? t('miniGames.questionLabel', { n: currentIndex + 1, defaultValue: 'QUESTION {{n}}' })
                : `${t('miniGames.culturalQuiz')} · ${t(THEME_META[selectedTheme].labelKey).toUpperCase()}`}
            </div>
            <p className="cq-niche__text">{quizQuestionText(currentQuestion.question, i18n.language)}</p>
          </div>

          {/* Réponses en colonne, avec lettres */}
          <div className="cq-options" role="radiogroup">
            {currentQuestion.options.map((option, i) => {
              const isSelected = answerState.selected === option;
              const isAnswer   = option === currentQuestion.answer;
              let state = '';
              if (hasAnswered) {
                if (isAnswer)        state = ' is-correct';
                else if (isSelected) state = ' is-wrong';
                else                 state = ' is-muted';
              } else if (pendingOption === option) {
                state = ' is-selected';
              }
              return (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={hasAnswered ? isSelected : pendingOption === option}
                  className={`cq-option${state}`}
                  onClick={() => setPendingOption(option)}
                  disabled={hasAnswered}
                >
                  <span className="cq-option__mark" aria-hidden="true">
                    {hasAnswered && isAnswer ? <Check size={16} strokeWidth={2.6} />
                      : hasAnswered && isSelected ? <X size={16} strokeWidth={2.6} />
                      : String.fromCharCode(65 + i)}
                  </span>
                  <span className="cq-option__text">{quizOptionText(currentQuestion.question, option, i18n.language)}</span>
                  {hasAnswered && isAnswer && (
                    <span className="cq-option__tag">{t('miniGames.correctTag', 'JUSTE')}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Correction : « Le savais-tu ? » */}
          {hasAnswered && currentQuestion.explanation && (
            <div className="cq-fact">
              <div className="cq-fact__kicker">{t('miniGames.didYouKnow', 'LE SAVAIS-TU ?')}</div>
              <p className="cq-fact__text">{quizExplanationText(currentQuestion.question, currentQuestion.explanation, i18n.language)}</p>
            </div>
          )}
        </div>

        <div className="cq-footer">
          {hasAnswered ? (
            <button type="button" className="cq-cta" onClick={handleNext}>
              {currentIndex + 1 >= questions.length ? t('miniGames.seeResults') : t('miniGames.nextQuestion')}
            </button>
          ) : (
            <button
              type="button"
              className="cq-cta"
              disabled={pendingOption === null}
              onClick={() => pendingOption !== null && handleAnswer(pendingOption)}
            >
              {t('miniGames.validate', 'Valider')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Rendu : résultats ────────────────────────────────────────────
  if (screen === 'results' && selectedTheme) {
    const badgeColor = THEME_META[selectedTheme].color;
    const badgeLabel = `${THEME_META[selectedTheme].emoji} ${t(THEME_META[selectedTheme].labelKey)} — ${countryName} ${countryData.countryFlag}`;

    let resultEmoji   = '😔';
    let resultMessage = t('miniGames.results.keepLearning');
    if (percentage >= 80)      { resultEmoji = '🏆'; resultMessage = t('miniGames.results.excellent'); }
    else if (percentage >= 60) { resultEmoji = '🌟'; resultMessage = t('miniGames.results.wellDone'); }
    else if (percentage >= 40) { resultEmoji = '📖'; resultMessage = t('miniGames.results.notBad'); }

    return (
      <div className="minigames-container" data-theme={theme}>

        <div className="results-screen">
          <div className="results-emoji"><EmojiText text={resultEmoji} /></div>
          <h2 className="results-title">{t('miniGames.results.quizDone')}</h2>

          <div className="results-theme-badge" style={{ '--theme-color': badgeColor } as React.CSSProperties}>
            <EmojiText text={badgeLabel} />
          </div>

          <div className="results-score-circle">
            <span className="results-score-number">{score}</span>
            <span className="results-score-total">/ {questions.length}</span>
          </div>
          <div className="results-percentage">{percentage}%</div>

          <p className="results-message">{resultMessage}</p>

          <div className="results-bar-track">
            <div
              className="results-bar-fill"
              style={{ width: `${percentage}%` }}
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>

          <div className="results-actions">
            <button className="results-btn results-btn-primary" onClick={handleReplay}>
              <EmojiText text={t('quiz.replay') as string} />
            </button>
            {onBack ? (
              <button className="results-btn results-btn-secondary" onClick={onBack}>
                {t('conversation.results.backToTrail')}
              </button>
            ) : (
              <button className="results-btn results-btn-secondary" onClick={handleBackToThemeSelect}>
                <EmojiText text={t('miniGames.results.changeTheme') as string} />
              </button>
            )}
            {onChallengeFriend && (
              <button
                className="results-btn results-btn-challenge"
                onClick={() => onChallengeFriend('cultural')}
              >
                <Picto name="epeesDuel" /> {t('miniGames.challengeFriend')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MiniGames;
